import React from 'react'
import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'

interface Step {
  label: string
  description?: string
}

interface StepperProps {
  steps: Step[]
  currentStep: number
  className?: string
}

export const Stepper: React.FC<StepperProps> = ({ steps, currentStep, className }) => {
  return (
    <div className={cn("w-full", className)}>
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const stepNumber = index + 1
          const isCompleted = index < currentStep
          const isCurrent = index === currentStep
          const isUpcoming = index > currentStep

          return (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center relative">
                {/* Glow effect for current step */}
                {isCurrent && (
                  <div className="absolute inset-0 -z-10 blur-xl opacity-50">
                    <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-gradient-to-r from-blue-400 via-primary to-purple-400 animate-pulse" />
                  </div>
                )}

                {/* Step Circle */}
                <div
                  className={cn(
                    "flex h-8 w-8 md:h-10 md:w-10 items-center justify-center rounded-full border-2 text-xs md:text-sm font-bold transition-all duration-300 relative overflow-hidden",
                    {
                      "border-emerald-500 bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500 text-white shadow-lg shadow-emerald-200 dark:shadow-emerald-900/50": isCompleted,
                      "border-primary bg-gradient-to-br from-blue-500 via-primary to-purple-500 text-white shadow-xl shadow-primary/50 scale-110 ring-2 md:ring-4 ring-primary/20": isCurrent,
                      "border-border bg-muted/50 text-muted-foreground": isUpcoming,
                    }
                  )}
                >
                  {/* Shimmer effect for current step */}
                  {isCurrent && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
                  )}

                  {isCompleted ? (
                    <Check className="h-4 w-4 md:h-5 md:w-5 animate-in zoom-in duration-300" />
                  ) : (
                    <span className="font-bold relative z-10">{stepNumber}</span>
                  )}
                </div>

                {/* Step Text */}
                <div className="mt-1.5 md:mt-2 text-center max-w-[70px] md:max-w-[100px]">
                  <div
                    className={cn(
                      "text-xs md:text-sm font-semibold transition-colors duration-300 leading-tight",
                      {
                        "text-emerald-600 dark:text-emerald-400": isCompleted,
                        "text-primary font-bold": isCurrent,
                        "text-muted-foreground": isUpcoming,
                      }
                    )}
                  >
                    {step.label}
                  </div>
                  {step.description && (
                    <div className={cn(
                      "hidden md:block text-xs mt-0.5 transition-colors duration-300",
                      {
                        "text-emerald-500 dark:text-emerald-400": isCompleted,
                        "text-primary/80 font-medium": isCurrent,
                        "text-muted-foreground/70": isUpcoming,
                      }
                    )}>
                      {step.description}
                    </div>
                  )}
                </div>
              </div>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="flex-1 mx-2 md:mx-4 flex items-center -mt-6 md:-mt-7">
                  <div className="relative w-full h-1 md:h-1.5">
                    {/* Background line */}
                    <div className="absolute inset-0 bg-border rounded-full" />

                    {/* Progress line with gradient */}
                    <div
                      className={cn(
                        "absolute inset-0 rounded-full transition-all duration-700 ease-out",
                        {
                          "bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 shadow-sm": index < currentStep,
                          "bg-gradient-to-r from-blue-500 via-primary to-purple-500 w-1/2": index === currentStep,
                          "w-0": index > currentStep,
                        }
                      )}
                    >
                      {/* Shimmer effect for active line */}
                      {index <= currentStep && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}